// This file is only used in the viz-client.js output. Viz-server still uses the original
// `@graphistry/common` logger. This is done via a WebPack module alias in viz-client's build.

import {
    each as _each,
    omit as _omit,
    merge as _merge,
    extend as _extend,
    isEmpty as _isEmpty,
    isObject as _isObject
} from 'lodash';

const pino = require('pino');

const userInfo = window.graphistryClientId ? { cid: window.graphistryClientId } : null;

export const nameFromLevel = pino().levels.labels;

// Preserve the original console.* functions, in case they get monkey-patched elsewhere
export const originalConsole = window.originalConsole || _merge({}, console);
window.originalConsole = originalConsole;

const levelToConsole = {
    trace: 'debug',
    debug: 'debug',
    info: 'info',
    warn: 'warn',
    error: 'error',
    fatal: 'error'
};

class BrowserConsoleStream {
    constructor() {
        this.fieldsToOmit = [
            'v',
            'name',
            'fileName',
            'pid',
            'hostname',
            'level',
            'module',
            'time',
            'msg'
        ];
    }

    write(rec) {
        const levelName = nameFromLevel[rec.level];
        const method = levelToConsole[levelName] || 'log';
        const prunedRec = _omit(rec, this.fieldsToOmit);

        if (_isEmpty(prunedRec)) {
            originalConsole[method](rec.msg);
        } else if ('err' in prunedRec) {
            const e = new Error(rec.err.message);
            e.stack = rec.err.stack;
            rec.msg === rec.err.message
                ? originalConsole[method](e)
                : originalConsole[method](rec.msg, e);
        } else {
            originalConsole[method](rec.msg, prunedRec);
        }
    }
}

class BrowserForwarderStream {
    constructor() {}

    write(rec) {
        const ajax = new XMLHttpRequest();
        const basePath = process.env.STUB_DEPENDENCIES === 'true' || process.env.STUB_DEPENDENCIES === true
            ? (process.env.BASE_PATH || 'http://localhost:3000') // stub dev: build-time server origin
            : (window.graphistryPath || '');
        ajax.open('POST', `${basePath}/error`, true);
        ajax.setRequestHeader('Content-type', 'application/json');
        // rec is the pino log record object (not a string), and it may hold a raw Error whose
        // message/stack are non-enumerable, so a plain JSON.stringify would silently drop them.
        // Serialize with an Error-aware replacer so the message and stack survive to the server.
        let payload;
        try {
            payload = JSON.stringify(rec, function (key, value) {
                if (value instanceof Error) {
                    return { name: value.name, message: value.message, stack: value.stack };
                }
                return value;
            });
        } catch (e) {
            // Circular or otherwise unserializable record: send a minimal descriptor rather than
            // drop the report. Do not console.error here: it reroutes to this transport and loops.
            payload = JSON.stringify({
                level: rec && rec.level,
                time: rec && rec.time,
                module: rec && rec.module,
                msg: rec && rec.msg,
                serializationError: 'client error record could not be fully serialized'
            });
        }
        ajax.send(payload);
    }
}

////////////////////////////////////////////////////////////////////////////////
// Parent logger
//
// A global singleton logger that all module-level loggers are children of.
////////////////////////////////////////////////////////////////////////////////

// Normally pulled from window.localStorage, if set. However, since trying to use that API when it's
// not available can lead to a crash, add some extra logic to detect that and default to 'info'.
function getClientLogLevel() {
    // From https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
    try {
        var storage = window['localStorage'];
        var x = '__storage_test__';
        storage.setItem(x, x);
        storage.removeItem(x);

        return window.localStorage.debugLevel || 'error';
    } catch (e) {
        console.warn('Cannot read localStorage to set LOG_LEVEL, defaulting to "error"');
        return 'error';
    }
}

// The parent logger is the parent of all other loggers. It sends all 'warn' or higher level
// messages to the server Metadata fields, etc. should be added to it, to ensure all subsequent log
// messages include those fields.
const bServer = new BrowserForwarderStream();
const bConsole = new BrowserConsoleStream();
function toBoth(rec) { 
    bConsole.write(rec);
    bServer.write(rec);
}
const clientParentLogger = pino({
    name: 'viz-client',
    browser: {
        write: {
            error: toBoth,
            fatal: toBoth
        }
    },
    ...(userInfo ? {userInfo} : {})
});

// The console logger is the parent of all of our app-code loggers (i.e., the parent of loggers
// created via `logger.createLogger()`). It logs all messages of a configured level or higher (by
// default 'info') to the browser console. Since it is a child of `clientParentLogger`, it also logs
// all >= 'warn' messages to the server as well.
const consoleLogLevel = getClientLogLevel();
const consoleLogger = clientParentLogger.child({
    level: consoleLogLevel
});

export function createDirectLogger(module, filename) {
    return clientParentLogger.child({ module, filename });
}

export function createLogger(module, fileName) {
    return consoleLogger.child({ module, fileName }, true);
}